import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/features/auth/store'
import { StageChangeDialog } from '@/features/recruitment/StageChangeDialog'
import {
  ALLOWED_STAGE_TRANSITIONS,
  APPLICATION_STAGE_LABELS,
  deleteApplication,
  getApplication,
  updateApplication,
  type ApplicationRead,
} from '@/features/recruitment/api'
import { toast } from '@/lib/toast'

const fail = (error: unknown) =>
  toast.error(
    error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
  )

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  )
}

/**
 * One application: candidate, posting, stage, and its own edit trail through the audit
 * log every `AuditedMixin` row already carries -- there is no separate stage-history
 * table, see the plan. Notes are the only field this screen lets anyone type; the stage
 * only moves through `StageChangeDialog`, never a plain edit.
 */
export function ApplicationDetail() {
  const { applicationId = '' } = useParams()
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [moving, setMoving] = useState(false)

  const {
    data: application,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => getApplication(applicationId),
    retry: false,
  })

  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!application) return
    setNotes(application.notes ?? '')
  }, [application?.id, application?.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps

  const settled = (message: string) => (updated: ApplicationRead) => {
    queryClient.setQueryData(['application', applicationId], updated)
    void queryClient.invalidateQueries({ queryKey: ['applications'] })
    toast.success(message)
  }

  const save = useMutation({
    mutationFn: () => updateApplication(applicationId, { notes: notes || null }),
    onSuccess: settled('Saved.'),
    onError: fail,
  })

  const withdraw = useMutation({
    mutationFn: () => deleteApplication(applicationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      toast.success('Withdrawn.')
      void navigate('/recruitment/applications')
    },
    onError: fail,
  })

  if (isLoading) return <Spinner />
  if (error || !application) {
    return (
      <Alert tone="error" title="Could not open this application">
        {error instanceof ApiError ? error.message : 'It may have been withdrawn.'}
      </Alert>
    )
  }

  const canUpdate = can('performance_reviews', 'update')
  const canDelete = can('performance_reviews', 'delete')
  const hasNextStage = ALLOWED_STAGE_TRANSITIONS[application.stage].length > 0

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/recruitment/applications"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All applications
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {application.candidate_name ?? 'Application'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {application.job_posting_title ?? 'Job posting'} ·{' '}
            {APPLICATION_STAGE_LABELS[application.stage]}
          </p>
        </div>
        {application.stage === 'rejected' && application.rejection_reason && (
          <div className="max-w-sm text-right text-sm text-slate-500 dark:text-slate-400">
            <span className="block font-medium text-danger">Rejected</span>
            {application.rejection_reason}
          </div>
        )}
      </header>

      <div className="flex flex-wrap gap-2">
        {canUpdate && hasNextStage && <Button onClick={() => setMoving(true)}>Move stage</Button>}
        {canDelete && (
          <Button variant="ghost" onClick={() => withdraw.mutate()} loading={withdraw.isPending}>
            Withdraw
          </Button>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <Row label="Candidate" value={application.candidate_name ?? '—'} />
            <Row label="Job posting" value={application.job_posting_title ?? '—'} />
            <Row label="Stage" value={APPLICATION_STAGE_LABELS[application.stage]} />
            <Row label="Applied" value={new Date(application.applied_at).toLocaleDateString()} />
            <Row
              label="In this stage since"
              value={new Date(application.current_stage_since).toLocaleDateString()}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Notes" />
          <CardBody className="space-y-4">
            <Field label="Notes">
              {(props) => (
                <Input
                  {...props}
                  disabled={!canUpdate}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              )}
            </Field>
            {canUpdate && (
              <Button onClick={() => save.mutate()} loading={save.isPending}>
                Save
              </Button>
            )}
          </CardBody>
        </Card>
      </div>

      <StageChangeDialog open={moving} application={application} onClose={() => setMoving(false)} />
    </div>
  )
}
